import { createContext, useContext, useEffect } from 'react';
import { useRecoilState as useState } from 'recoil';

import { useCurrentUser } from 'hooks/useCurrentUser';
import { splitIoState } from './state';
import { getClientWithState } from './StatefulSplitClient';
import type { StatefulSplitClient, StatefulSplitClientState } from './StatefulSplitClient';
import { logger } from 'modules/logger';
import { IBrowserClient } from '@splitsoftware/splitio/types/splitio';

// @ts-expect-error [EN-10263] - TS2322 - Type 'Context<null>' is not assignable to type 'Context<StatefulSplitClient | null>'.
export const MockSplitClientContext: React.Context<StatefulSplitClient | null> =
  createContext(null);

export const useSplitClient = (): [Nullable<IBrowserClient>, boolean, boolean] => {
  // To get around exhaustive-deps we can use the 'useState' alias but using
  // recoil state (as they are equivalent). This way we do not have to ignore the line
  const [state, setState] = useState(splitIoState);
  const { data } = useCurrentUser();
  const email = data?.me.email;

  const mockedSplitClient = useContext(MockSplitClientContext);

  useEffect(() => {
    if (email == null) {
      return;
    }

    const statefulClient = getClientWithState(email);

    setState((state) => {
      if (
        state.email === email &&
        state.client === statefulClient &&
        state.ready === (statefulClient.state === 'ready') &&
        state.timeout === (statefulClient.state === 'timeout')
      ) {
        return state;
      }

      return {
        ...state,
        client: statefulClient,
        email,
        ready: statefulClient.state === 'ready',
        timeout: statefulClient.state === 'timeout',
      };
    });

    // Subscribe unless the client has already settled as `ready`. `timeout` is NOT terminal:
    // SDK_READY_TIMED_OUT only means the SDK missed its readyTimeout window, and SDK_READY still
    // fires afterwards once treatments arrive. Gating this on `state === 'pending'` meant any
    // consumer mounting after the timeout (the viewer mounts long after a 5s timeout would have
    // fired) never subscribed, so the later SDK_READY could not reach Recoil and `timeout` stayed
    // true for the rest of the session.
    if (statefulClient.state !== 'ready') {
      const onStateChange = (nextClientState: StatefulSplitClientState) => {
        if (nextClientState === 'ready') {
          // `ready` supersedes an earlier timeout. useSplitFlag short-circuits on `timeout` before
          // it checks `ready`, so leaving the flag set here pins every flag to its FFDefaults value
          // even though the client itself holds the real treatments.
          setState((prev) => ({ ...prev, ready: true, timeout: false }));
        } else if (nextClientState === 'timeout') {
          setState((prev) => ({ ...prev, timeout: true }));
        }
      };

      statefulClient.onStateChange(onStateChange);
      return () => statefulClient.offStateChange(onStateChange);
    } else {
      // this message is spammy so it will hide in debug
      logger.debug(`Legacy Split Client: already ${statefulClient.state}`);
    }
  }, [email]);

  return mockedSplitClient
    ? [
        mockedSplitClient.splitClient,
        mockedSplitClient.state === 'ready',
        mockedSplitClient.state === 'timeout',
      ]
    : [state.email === email ? state.client?.splitClient : null, state.ready, state.timeout];
};
